import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApiData, useAppDispatch } from 'reducers/hooks';
import { duplicateProject, deleteProjectThunk, getProjectsThunk, postProjectThunk, updateProjectThunk } from 'reducers/fell/projects/action';
import { ProjectType } from '../../lib/types';
import { ColumnDef } from '@tanstack/react-table';
import { EditableTextCell } from 'components/Table/EditableTextCell'
import { DeleteRowAction } from 'components/Table/DeleteRowAction'
import { NavigateAction } from 'components/Table/NavigateAction'
import { DuplicateRowAction } from 'components/Table/DuplicateRowAction'
import { isNameLengthTooLong } from '../../lib/helpers';
import { useNavigate } from 'react-router-dom';
import { Row } from "@tanstack/react-table";
import type { QuickStartState } from '../../components/QuickStartModal/QuickStartModal';
import { getQuickStartThunk, postQuickStartThunk } from 'reducers/fell/tools/toolsActions';
import type { QuickStartResponse, QuickStartProject } from 'lib/types';


type ProjectData = {
    projectsData: ProjectType[],
    handleAddProject: () => void,
    handleDeleteProjectRow: (rowId: number) => void,
    handleModalConfirmProjectDeletion: () => void,
    handleModalCancelProjectDeletion: () => void,
    handleDuplicateProjectRow: (projectId: number) => void,
    tableColumns: ColumnDef<ProjectType>[],
    showModal: boolean,
    maxNameLength: number,
    hasNameError: boolean,
    handleValidationMessageCloseEvent: () => void;
    quickStart: QuickStartState,
}

export const ProjectsInitialization = (): ProjectData => {
    const dispatch = useAppDispatch();
    const [showModal, setShowModal] = useState(false);
    const [qsOpen, setQsOpen] = useState(false);
    const [qsSubmitting, setQsSubmitting] = useState(false);
    const [qsProjectOptions, setQsProjectOptions] = useState<Array<{ value: string; label?: string }>>([]);
    const [qsQuickStartProjects, setQsQuickStartProjects] = useState<Record<string, QuickStartProject>>({});

    const [projectIdToDelete, setProjectIdToDelete] = useState<number | null>(null);
    const [projectsData, setProjectsData] = useState<ProjectType[]>([]);
    const navigate = useNavigate();
    const apiData = useApiData(getProjectsThunk(), state => state.projects.data, state => state.projects.projectsStatus, navigate);
    const maxNameLength = 100;
    const [hasNameError, setHasNameError] = useState(false);

    const siteOptions = useMemo(() => [], [] as Array<{ value: string; label?: string }>);

    const openQuickStart = useCallback(async () => {
        try {
            const data: QuickStartResponse = await dispatch(getQuickStartThunk()).unwrap();

            const projectArray = data.sortedProjects ?? Object.values(data.projects ?? {});

            const byName: Record<string, QuickStartProject> = {};

            projectArray.forEach(p => {
                if (p?.name) byName[p.name] = p;
            });
            setQsQuickStartProjects(byName);

            const options = projectArray
                .filter(p => p?.name)
                .map(p => ({ value: p.name, label: p.name }));
            setQsProjectOptions(options);

        } catch (e: any) {
            setQsProjectOptions([]);
            setQsQuickStartProjects({});
        } finally {
            setQsOpen(true);
        }
    }, [dispatch]);


    const closeQuickStart = useCallback(() => {
        if (qsSubmitting) return;
        setQsOpen(false);
    }, [qsSubmitting]);

    const submitQuickStart = useCallback(async (payload: {
        projectName: string;
        siteName: string;
        siteDesignName: string;
    }) => {
        setQsSubmitting(true);
        let navigated = false;
        try {
            const selectedProject = qsQuickStartProjects[payload.projectName];
            const projectId = selectedProject?.id ?? 0;
            const selectedSite = selectedProject?.sites ? Object.values(selectedProject.sites).find(s => s?.name === payload.siteName) : undefined;
            const siteId = selectedSite?.id ?? 0;

            const postPayload = {
                projectId,
                projectName: payload.projectName || '',
                siteId,
                siteName: payload.siteName || '',
                siteDesignName: payload.siteDesignName || '',
            };

            const result = await dispatch(postQuickStartThunk({ payload: postPayload })).unwrap();

            if (result?.objectUrl) {
                // Refresh projects before navigating — ensures projectName is available
                await dispatch(getProjectsThunk());

                navigated = true;
                const url = new URL(result.objectUrl);
                const designPath = url.pathname.replace(/^\/api/, "");

                // Pass names as fallback in case Redux hasn’t updated yet
                navigate(designPath, {
                    state: {
                        projectName: payload.projectName,
                        siteName: payload.siteName,
                        siteDesignName: payload.siteDesignName,
                    },
                });

                return;
            }

            await dispatch(getProjectsThunk());
            setQsOpen(false);
        } catch (e: any) { }
        finally {
            if (!navigated) setQsSubmitting(false);
        }
    }, [dispatch, navigate, qsQuickStartProjects]);


    useEffect(() => {
        setProjectsData(apiData);
    }, [apiData]);

    const handleAddProject = async () => {
        try {

            await dispatch(postProjectThunk({
                rejectValue: "Some default value or an empty string"
            }));

            await dispatch(getProjectsThunk());

        } catch (error) {
            console.error("Error adding project:", error);
            navigate('/error-page');
        }
    };

    const handleDeleteProjectRow = (projectId: number) => {
        if (projectId >= 0) {
            setProjectIdToDelete(projectId);
            setShowModal(true);
        }
    };

    const handleModalConfirmProjectDeletion = async () => {
        if (projectIdToDelete !== null) {
            const projectToDelete = projectsData.find(project => project.id === projectIdToDelete); //check if the project exist, if yes then we delete it.
            if (projectToDelete) {
                const projectId = projectToDelete.id.toString();

                try {
                    await dispatch(deleteProjectThunk({
                        projectId
                    }));

                    await dispatch(getProjectsThunk());

                } catch (error) {
                    console.error("Error deleting project:", error);
                    navigate('/error-page');
                }
            }
        }

        setShowModal(false);
    };

    const handleModalCancelProjectDeletion = () => {
        setShowModal(false);
    };

    const handleDuplicateProjectRow = async (projectId: number) => {
        if (projectId >= 0) {
            const projectToDuplicate = projectsData.find(project => project.id === projectId);
            if (projectToDuplicate) {
                const checkNameTooLong = isNameLengthTooLong(projectToDuplicate.name, maxNameLength - 8)
                setHasNameError(checkNameTooLong)  // "Copy of " = 8 characters for duplicate site design name
                if (!checkNameTooLong) {
                    try {
                        await dispatch(duplicateProject({
                            projectId: projectToDuplicate.id
                        }))

                        await dispatch(getProjectsThunk());

                    } catch (error) {
                        console.error("Error duplicating project:", error);
                        navigate('/error-page');
                    }
                }
            }
        }
    };

    const handleUpdateProject = async (row: Row<any>, columnId: string, value: string) => {

        if (row) {
            const updatedProjectDataIndex = projectsData.findIndex(project => project.id === row.original.id);

            if (updatedProjectDataIndex >= 0) {
                const updatedProjectData = [...projectsData];
                updatedProjectData[updatedProjectDataIndex] = { ...updatedProjectData[updatedProjectDataIndex], [columnId]: value };

                if (updatedProjectData) {
                    // to be sure that the name of the site design after updating is still inferior than 50 characters
                    const currentProject = projectsData[updatedProjectDataIndex];
                    const updatedProject = updatedProjectData[updatedProjectDataIndex];

                    if (currentProject && updatedProject) {
                        if (currentProject.name !== updatedProject.name) { //checks if the name has been changed otherwise we don't post anything
                            const checkNameTooLong = isNameLengthTooLong(updatedProject.name, maxNameLength)
                            setHasNameError(checkNameTooLong)
                            if (!checkNameTooLong) {
                                setProjectsData(updatedProjectData);

                                try {
                                    await dispatch(updateProjectThunk(updatedProject))
                                    await dispatch(getProjectsThunk());
                                } catch (error) {
                                    console.error("Error updating project:", error);
                                    navigate('/error-page');
                                }
                            }
                        }
                    }
                }
            }
        }
    };

    const handleValidationMessageCloseEvent = () => {
        setHasNameError(false)
    }

    const tableColumns = [
        {
            accessorKey: 'primaryActions',
            header: '',
            cell: ({ row }) => (
                <div className="row-action-container">
                    <DuplicateRowAction row={row} duplicateClickHandler={handleDuplicateProjectRow} />
                    <NavigateAction rowIndex={row.index} url={`/project/${row.original.id}/site`} />
                </div>
            )
        },
        {
            accessorKey: 'name',
            header: 'Project',
            cell: ({ row, column }) => (
                <EditableTextCell
                    inputId={"xsd__e2e-projects-table-row-" + row.id + "-cell-input-" + column.id}
                    value={row.original.name}
                    row={row}
                    column={column}
                    updateCellData={handleUpdateProject}
                    maxLength={maxNameLength}
                />
            ),
        },
        {
            header: 'Date Created',
            accessorKey: 'formattedCreatedUtc',
        },
        //{
        //    Header: "Modified",                    // TO BE ADDED LATER WHEN IT IS CORRECTLY HANLDED BY FELL
        //    accessor: "formattedUpdatedUtc"
        //}
        {
            accessorKey: 'secondaryActions',
            header: '',
            cell: ({ row }) => (
                <div className="row-action-container">
                    <DeleteRowAction row={row} deleteClickHandler={handleDeleteProjectRow} />
                </div>
            )
        },
    ];

    const quickStart = {
        open: qsOpen,
        openModal: openQuickStart,
        closeModal: closeQuickStart,
        submit: submitQuickStart,
        projectOptions: qsProjectOptions,
        siteOptions,
        quickStartProjects: qsQuickStartProjects,
        submitting: qsSubmitting,
    };

    return {
        projectsData: projectsData,
        handleAddProject,
        handleDeleteProjectRow,
        handleModalConfirmProjectDeletion,
        handleModalCancelProjectDeletion,
        handleDuplicateProjectRow,
        tableColumns,
        showModal,
        maxNameLength,
        hasNameError,
        handleValidationMessageCloseEvent,
        quickStart
    }
}
